'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, CampaignsTable, ContentPreviewModal } from './components';
import { CampaignsFilters, RevshareCampaign, AvailableModel, CampaignEditData } from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import { partnerRevshareService } from '../../../api';
import { showPartnerSuccess, showError } from '../../../utils/toast';

// Map work_content category keys to display labels
const getCategoryLabel = (category: string): string => {
  const labels: Record<string, string> = {
    nonude: 'No nude',
    tt: 'TikTok',
    lingerie: 'Lingerie',
    lite_nude: 'Lite nude',
    full_nude: 'Full nude',
  };
  return labels[category] || category;
};

const RevshareCampaigns: React.FC = () => {
  const { t } = useTranslations();

  // Ref to store abort controller for cancelling previous requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Главная', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.partnerCampaigns.title') || 'Кампании', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<RevshareCampaign[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelSearchQuery, setModelSearchQuery] = useState('');

  // Filters
  const [filters, setFilters] = useState<CampaignsFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');
  const [localFansFrom, setLocalFansFrom] = useState('');
  const [localRevenueFrom, setLocalRevenueFrom] = useState('');

  // Modal state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<RevshareCampaign | null>(null);

  // Stop campaign modal state
  const [isStopModalOpen, setIsStopModalOpen] = useState(false);
  const [campaignToStop, setCampaignToStop] = useState<RevshareCampaign | null>(null);
  const [isStoppingCampaign, setIsStoppingCampaign] = useState(false);

  // Load available models from API
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await partnerRevshareService.getCompaniesModels({
        page: 1,
        page_size: 50,
        search: search || undefined,
      });
      if (response.objects) {
        setAvailableModels(
          response.objects.map((acc) => ({
            id: acc._id,
            name: `${String(acc.of_user_id).slice(-4)}`,
            username: acc.of_user_name || '',
            avatar: acc.avatar || '',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Initial load of models - only once on mount
  useEffect(() => {
    fetchAvailableModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.account !== '' ||
      filters.status !== '' ||
      filters.sortBy !== '' ||
      filters.campaignType !== '' ||
      filters.trafficSource !== '' ||
      filters.fansFrom !== null ||
      filters.incomeFrom !== null
    );
  }, [filters]);

  // Load campaigns when dependencies change - fetch directly in useEffect to avoid double render
  useEffect(() => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);

    const fetchData = async () => {
      try {
        // Validate and limit page_size to maximum 50
        const validPageSize = Math.min(Math.max(1, pageSize), 50);
        const validPage = Math.max(1, currentPage);

        const params: any = {
          page: validPage,
          page_size: validPageSize,
        };

        // Add sort_by - use default if not specified (newest first)
        const sortBy = filters.sortBy?.trim() || 'created_at';
        if (sortBy) {
          params.sort_by = sortBy;
        }

        // Add search as ID filter (only if not empty)
        if (filters.search?.trim()) {
          params.id = filters.search.trim();
        }

        // Add status filter - map frontend status to API status
        if (filters.status) {
          if (filters.status === 'active') {
            params.status = 'in_progress';
          } else if (filters.status === 'inactive') {
            params.status = 'completed';
          } else if (['in_moderation', 'waiting', 'canceled'].includes(filters.status)) {
            params.status = filters.status;
          }
        }

        // Add traffic source filter
        if (filters.trafficSource) {
          params.source = filters.trafficSource;
        }

        // Add campaign type (model type) - send directly to API
        if (filters.campaignType) {
          params.model_type = filters.campaignType;
        }

        // Add subscribers from filter
        if (filters.fansFrom !== null) {
          params.subs_from = filters.fansFrom;
        }

        // Add income filter
        if (filters.incomeFrom !== null) {
          params.income = filters.incomeFrom;
        }

        // Add model filter (account filter)
        if (filters.account) {
          params.models = [filters.account];
        }

        const response = await partnerRevshareService.getOrders(params);

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        setCampaigns(response.objects as unknown as RevshareCampaign[]);
        setTotalCount(response.count);
        setTotalIncome(parseFloat(response.total_amount || '0') || 0);
      } catch (error: any) {
        // Don't show error if request was aborted
        if (error?.name === 'AbortError' || abortController.signal.aborted) {
          return;
        }
        console.error('Error fetching campaigns:', error);
        setCampaigns([]);
        setTotalCount(0);
      } finally {
        // Only update loading state if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup: cancel request on unmount or when dependencies change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentPage, pageSize, filters]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce search - only update if value actually changed
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        // Only update if value actually changed
        if (prev.search !== localSearch) {
          return { ...prev, search: localSearch };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Debounced fansFrom
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = localFansFrom === '' ? null : parseInt(localFansFrom, 10);
      setFilters((prev) => {
        // Only update if value actually changed
        if (prev.fansFrom !== value) {
          return { ...prev, fansFrom: value };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localFansFrom]);

  // Debounced revenueFrom
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = localRevenueFrom === '' ? null : parseInt(localRevenueFrom, 10);
      setFilters((prev) => {
        // Only update if value actually changed
        if (prev.incomeFrom !== value) {
          return { ...prev, incomeFrom: value };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localRevenueFrom]);

  const handleFilterChange = useCallback((key: keyof CampaignsFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setLocalFansFrom('');
    setLocalRevenueFrom('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleShowContent = useCallback((campaign: RevshareCampaign) => {
    setSelectedCampaign(campaign);
    setIsPreviewModalOpen(true);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
    setSelectedCampaign(null);
  }, []);

  const handleStopCampaign = useCallback((campaign: RevshareCampaign) => {
    setCampaignToStop(campaign);
    setIsStopModalOpen(true);
  }, []);

  const handleConfirmStopCampaign = useCallback(async () => {
    if (!campaignToStop) return;

    setIsStoppingCampaign(true);
    try {
      const orderId = campaignToStop._id || campaignToStop.id;
      if (!orderId) {
        console.error('No order ID found');
        showError(
          t('partner.revshare.campaigns.stopModal.error') || 'Не удалось остановить кампанию'
        );
        return;
      }

      await partnerRevshareService.cancelOrder(orderId);

      // Close modal
      setIsStopModalOpen(false);
      setCampaignToStop(null);

      // Show success toast
      showPartnerSuccess(
        t('partner.revshare.campaigns.stopModal.success') || 'Изменения успешно сохранены'
      );

      // Refresh campaigns list - trigger re-fetch by updating filters
      setFilters((prev) => ({ ...prev }));
    } catch (error: any) {
      console.error('Error stopping campaign:', error);
      showError(
        error?.response?.data?.detail ||
          t('partner.revshare.campaigns.stopModal.error') ||
          'Не удалось остановить кампанию'
      );
    } finally {
      setIsStoppingCampaign(false);
    }
  }, [campaignToStop, t]);

  const handleCloseStopModal = useCallback(() => {
    if (!isStoppingCampaign) {
      setIsStopModalOpen(false);
      setCampaignToStop(null);
    }
  }, [isStoppingCampaign]);

  const handleEditCampaign = useCallback((campaign: RevshareCampaign, data: CampaignEditData) => {
    // The API call is now handled inside EditCampaignModal
    console.log('Campaign edited:', campaign._id, data);
  }, []);

  // Refresh campaigns list after edit
  const handleRefresh = useCallback(() => {
    setFilters((prev) => ({ ...prev }));
  }, []);

  const handlePublicLinksClick = useCallback(() => {
    // TODO: Implement public links modal
    console.log('Open public links modal');
  }, []);

  // Handle model search change - fetch from API
  const handleModelSearchChange = useCallback(
    (value: string) => {
      setModelSearchQuery(value);
      fetchAvailableModels(value);
    },
    [fetchAvailableModels]
  );

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6 ">
        <Typography variant="h4" className="mb-2">
          {t('partner.partnerCampaigns.title')}
        </Typography>
      </div>

      {/* Filters */}
      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        localFansFrom={localFansFrom}
        localRevenueFrom={localRevenueFrom}
        availableModels={availableModels}
        isLoading={modelsLoading}
        hasActiveFilters={hasActiveFilters}
        onSearchChange={setLocalSearch}
        onFansFromChange={setLocalFansFrom}
        onRevenueFromChange={setLocalRevenueFrom}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        onPublicLinksClick={handlePublicLinksClick}
        onModelSearchChange={handleModelSearchChange}
      />

      {/* Table */}
      <CampaignsTable
        campaigns={campaigns}
        isLoading={isLoading}
        totalCount={totalCount}
        totalIncome={totalIncome}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onShowContent={handleShowContent}
        onStopCampaign={handleStopCampaign}
        onEditCampaign={handleEditCampaign}
        onRefresh={handleRefresh}
      />

      {/* Content Preview Modal */}
      {selectedCampaign && (
        <ContentPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={handleClosePreviewModal}
          modelName={selectedCampaign.model?.of_user_name || ''}
          mediaUrls={selectedCampaign.model?.public_content || []}
          contentLinks={
            selectedCampaign.model?.work_content
              ? Object.entries(selectedCampaign.model.work_content)
                  .filter(([_, url]) => url && url.trim() !== '')
                  .map(([category, url]) => ({
                    category: getCategoryLabel(category),
                    url: url as string,
                  }))
              : []
          }
        />
      )}

      {/* Stop Campaign Confirmation Modal */}
      <ResponsiveModal
        isOpen={isStopModalOpen}
        onClose={handleCloseStopModal}
        size="md"
        classNames={{ base: 'rounded-4' }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1 pt-6 pb-4 px-4 md:px-6 text-xl font-medium text-auth-ink">
            {t('partner.revshare.campaigns.stopModal.title') || 'Вы уверены?'}
          </ModalHeader>
          <ModalBody className="pt-3 pb-6 px-4 md:px-6">
            <p className="text-sm text-auth-ink leading-[1.125rem]">
              {t('partner.revshare.campaigns.stopModal.description') ||
                'Вы уверены? Вы все равно будете зарабатывать деньги на своих подписчиках'}
            </p>
          </ModalBody>
          <ModalFooter className="px-4 md:px-6 py-4 gap-2">
            <Button
              variant="flat"
              color="danger"
              className="bg-auth-strength-weak/20 text-auth-strength-weak h-10 px-4 rounded-3 font-normal"
              onPress={handleCloseStopModal}
              isDisabled={isStoppingCampaign}
            >
              {t('global.buttons.cancel') || 'Отмена'}
            </Button>
            <Button
              color="primary"
              className="bg-partner text-white h-10 px-3.5 rounded-3 font-semibold"
              onPress={handleConfirmStopCampaign}
              isLoading={isStoppingCampaign}
            >
              {t('partner.revshare.campaigns.stopModal.confirm') || 'Продолжить'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
};

export default RevshareCampaigns;
